import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Globe, Instagram, MessageCircle, PhoneCall, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const info = [
  { icon: MapPin, label: "Διεύθυνση", value: "25ης Μαρτίου 64, Πετρούπολη" },
  { icon: Phone, label: "Τηλέφωνο", value: "210.50.65.275" },
  { icon: Mail, label: "Email", value: "fysikiepilogi.01@gmail.com" },
  { icon: Globe, label: "Ιστοσελίδα", value: "www.fysikiepilogi.gr" },
  { icon: Instagram, label: "Instagram", value: "Φυσική Επιλογή" },
  { icon: MessageCircle, label: "Messenger", value: "Φυσική Επιλογή" },
  { icon: PhoneCall, label: "Viber", value: "Φυσική Επιλογή" },
  { icon: Clock, label: "Ώρες", value: "Δευ–Παρ: 15:00 – 21:30\nΣαβ–Κυρ: 09:00 – 14:00" },
];

const initial = { name: "", email: "", phone: "", subject: "", message: "" };
export const CAREER_SUBJECT = "Συνάντηση επαγγελματικού προσανατολισμού";
const SUBJECTS = ["Πληροφορίες για εγγραφή", CAREER_SUBJECT, "Πρόγραμμα & δίδακτρα", "Διαγνωστικό τεστ", "Άλλο"];

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const [other, setOther] = useState(false);

  useEffect(() => {
    const h = (e) => { setOther(false); setForm((f) => ({ ...f, subject: e.detail })); };
    window.addEventListener("contact-subject", h);
    return () => window.removeEventListener("contact-subject", h);
  }, []);

  const onSubject = (e) => {
    const v = e.target.value;
    if (v === "Άλλο") { setOther(true); setForm({ ...form, subject: "" }); }
    else { setOther(false); setForm({ ...form, subject: v }); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Συμπληρώστε όνομα, email και μήνυμα.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Το μήνυμά σας στάλθηκε! Θα επικοινωνήσουμε σύντομα.");
      setForm(initial); setOther(false);
    } catch (err) {
      toast.error("Κάτι πήγε στραβά. Δοκιμάστε ξανά.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-border rounded-sm px-4 py-3 text-sm outline-none focus:border-primary transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-secondary/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-4">// Επικοινωνία</div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
              Ας ξεκινήσουμε τη διαδρομή προς την επιτυχία.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Κλείστε ένα δωρεάν ραντεβού γνωριμίας ή ζητήστε πληροφορίες για τα τμήματα. Είμαστε εδώ για εσάς.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-px bg-border border border-border rounded-sm overflow-hidden">
              {info.map((it) => (
                <div key={it.label} data-testid={`contact-info-${it.label}`} className="bg-white p-5">
                  <it.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  <div className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground mt-3">{it.label}</div>
                  <div className="font-medium text-sm mt-1 whitespace-pre-line">{it.value}</div>
                </div>
              ))}
            </div>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-white border border-border rounded-sm p-6 sm:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">Ονοματεπώνυμο *</label>
                <input data-testid="contact-name" name="name" value={form.name} onChange={onChange} className={`mt-2 ${inputCls}`} placeholder="Το όνομά σας" />
              </div>
              <div>
                <label className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">Email *</label>
                <input data-testid="contact-email" name="email" type="email" value={form.email} onChange={onChange} className={`mt-2 ${inputCls}`} placeholder="email@example.com" />
              </div>
              <div>
                <label className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">Τηλέφωνο</label>
                <input data-testid="contact-phone" name="phone" value={form.phone} onChange={onChange} className={`mt-2 ${inputCls}`} placeholder="69XXXXXXXX" />
              </div>
              <div>
                <label className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">Θέμα</label>
                <select
                  data-testid="contact-subject"
                  name="subject_select"
                  value={other ? "Άλλο" : (SUBJECTS.includes(form.subject) ? form.subject : "")}
                  onChange={onSubject}
                  className={`mt-2 ${inputCls}`}
                >
                  <option value="">— Επιλέξτε θέμα —</option>
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {other && (
                  <input data-testid="contact-subject-other" name="subject" value={form.subject} onChange={onChange} className={`mt-2 ${inputCls}`} placeholder="Γράψτε το θέμα σας" />
                )}
                {form.subject === CAREER_SUBJECT && (
                  <p data-testid="contact-career-hint" className="mt-2 text-xs text-primary leading-relaxed">
                    Αν έχετε κάνει τα τεστ του ΕΟΠΠΕΠ, γράψτε στο μήνυμα την τάξη του μαθητή και ότι έχετε τα αποτελέσματα — θα τα δούμε μαζί στη συνάντηση.
                  </p>
                )}
              </div>
            </div>
            <div className="mt-5">
              <label className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">Μήνυμα *</label>
              <textarea data-testid="contact-message" name="message" value={form.message} onChange={onChange} rows={5} className={`mt-2 ${inputCls} resize-none`} placeholder="Πείτε μας πώς μπορούμε να βοηθήσουμε..." />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={loading}
              className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono-accent text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-accent transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Αποστολή..." : "Αποστολή μηνύματος"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
